// Lesson_QuadraticInequalities_TeoriaPrzyklady.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import ParabolaShape from "../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../components/visuals/NumberLineRanges";

/** Delikatny separator przerywany (dopasowuje się do motywu przez currentColor) */
function DashedSep({ margin = "0.75rem", opacity = 0.35 }) {
  return (
    <hr
      role="separator"
      aria-hidden="true"
      style={{
        border: 0,
        borderTop: "1px dashed",
        borderColor: "var(--color-text)",
        opacity: 1,

        margin: `${margin} 0`,
      }}
    />
  );
}

export default function Lesson_QuadraticInequalities_TeoriaPrzyklady() {
  const intro = (
    <div className="space-y-3">
      <p>
        W nierównościach kwadratowych patrzymy, gdzie wykres paraboli
        <InlineMath math="\,y=ax^2+bx+c\," /> leży <strong>nad</strong> (dla
        <InlineMath math="\,f(x)&gt;0,\ f(x)\ge0" />) albo <strong>pod</strong> osią OX
        (dla <InlineMath math="\,f(x)&lt;0,\ f(x)\le0" />). Kierunek ramion zależy od
        <InlineMath math="\,a" />: gdy <InlineMath math="a>0" /> — do góry, gdy
        <InlineMath math="a<0" /> — w dół.
      </p>

      {/* Δ>0 */}
      <div className="space-y-1 text-center">
        <p className="font-medium">
          <InlineMath math="a>0,\ \Delta>0:\ \text{dwa miejsca zerowe }x_1<x_2" />
        </p>
        <ParabolaShape
          aSign={1}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          rootMode="two"
          rootLeft={-2}
          rootRight={2}
          curveColor="#f59e0b"
        />
        <p className="text-sm">
          Dla <InlineMath math="f(x)&gt;0" /> — na zewnątrz <InlineMath math="(-\infty,x_1)\cup(x_2,\infty)" />.
          Dla <InlineMath math="f(x)&lt;0" /> — między zerami <InlineMath math="(x_1,x_2)" />.
        </p>
      </div>

      <DashedSep />

      {/* Δ=0 */}
      <div className="space-y-1 text-center">
        <p className="font-medium">
          <InlineMath math="a<0,\ \Delta=0:\ \text{styczność w }x_0" />
        </p>
        <ParabolaShape
          aSign={-1}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          rootMode="one"
          tangentX={1}
          curveColor="#f59e0b"
        />
        <p className="text-sm">
          Cały wykres <strong>pod</strong> osią (dotyka w <InlineMath math="x_0" />).
          Zatem <InlineMath math="f(x)\le0" /> — dla wszystkich <InlineMath math="\mathbb{R}" />,
          a <InlineMath math="f(x)&lt;0" /> — dla <InlineMath math="\mathbb{R}\setminus\{x_0\}" />.
        </p>
      </div>

      <DashedSep />

      {/* Δ<0 */}
      <div className="space-y-1 text-center">
        <p className="font-medium">
          <InlineMath math="a>0,\ \Delta<0:\ \text{brak przecięć z OX}" />
        </p>
        <ParabolaShape
          aSign={1}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          rootMode="none"
          curveColor="#f59e0b"
        />
        <p className="text-sm">
          Cały wykres <strong>nad</strong> osią: <InlineMath math="f(x)&gt;0" /> i <InlineMath math="f(x)\ge0" /> dla
          wszystkich <InlineMath math="\mathbb{R}" />; odpowiednio <InlineMath math="f(x)&lt;0" /> — zbiór pusty.
        </p>
      </div>
    </div>
  );

  const examples = [
    {
  label: "Przykład 1",
  summary: "Ramiona do góry, dwa zera, znak „>” — rozwiązanie na zewnątrz",
  description: "Rozwiąż nierówność:",
  content: (
    <div className="space-y-3">
      <p><strong>Polecenie:</strong> <InlineMath math="x^2 - x - 6 \;>\; 0" /></p>

      <p><strong>Krok 1.</strong> Liczymy deltę i miejsca zerowe.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`\begin{aligned}
          a&=1,\quad b=-1,\quad c=-6\\
          \Delta&=b^2-4ac=(-1)^2-4\cdot1\cdot(-6)=1+24=25\\
          x_{1,2}&=\frac{-b\pm\sqrt{\Delta}}{2a}=\frac{1\pm5}{2}\;\Rightarrow\;x_1=-2,\;x_2=3
          \end{aligned}`
        } />
      </div>

      <p><strong>Krok 2.</strong> <InlineMath math="a=1>0" /> — ramiona do góry; dla „&gt;0” bierzemy obszary nad osią (na zewnątrz).</p>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <ParabolaShape
          aSign={1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          rootMode="two"
          rootLeft={-2}
          rootRight={3}
          labelLeftTex="-2"
          labelRightTex="3"
          highlightMode="outside"
          highlightColor="#a78bfa"
          curveColor="#f59e0b"
        />
      </div>

      <p><strong>Wniosek:</strong> <InlineMath math="x\in(-\infty,-2)\cup(3,\infty)" />.</p>

      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <NumberLineRanges
          lockPair
          showZero
          ranges={[
            { to: -2, rightClosed: false, rightLabelLatex: "-2" },
            { from: 3, leftClosed: false, leftLabelLatex: "3" },
          ]}
        />
      </div>
    </div>
  ),
},

    {
  label: "Przykład 2",
  summary: "Ramiona do góry, dwa zera, znak „<” — między zerami",
  description: "Rozwiąż nierówność:",
  content: (
    <div className="space-y-3">
      <p><strong>Polecenie:</strong> <InlineMath math="x^2 - x - 6 \;<\; 0" /></p>

      <p><strong>Krok 1.</strong> Te same obliczenia z delty — dla kompletności:</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`\begin{aligned}
          a&=1,\quad b=-1,\quad c=-6,\qquad \Delta=25\\
          x_{1,2}&=\frac{1\pm5}{2}\;\Rightarrow\;x_1=-2,\;x_2=3
          \end{aligned}`
        } />
      </div>

      <p><strong>Krok 2.</strong> <InlineMath math="a=1>0" /> — ramiona do góry; dla „&lt;0” bierzemy część pod osią (między zerami).</p>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <ParabolaShape
          aSign={1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          rootMode="two"
          rootLeft={-2}
          rootRight={3}
          labelLeftTex="-2"
          labelRightTex="3"
          highlightMode="inside"
          highlightColor="#a78bfa"
          curveColor="#f59e0b"
        />
      </div>

      <p><strong>Wniosek:</strong> <InlineMath math="x\in(-2,3)" />.</p>

      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <NumberLineRanges
          lockPair
          showZero
          ranges={[
            { from: -2, to: 3, leftClosed: false, rightClosed: false, leftLabelLatex: "-2", rightLabelLatex: "3" },
          ]}
        />
      </div>
    </div>
  ),
},

{
  label: "Przykład 3",
  summary: "Δ = 0, ramiona do góry — punkt styczności i wyrzucenie tego punktu",
  description: "Rozwiąż nierówności:",
  content: (
    <div className="space-y-3">
      <p><strong>Polecenia:</strong> <InlineMath math="x^2 - 4x + 4 \;\le\; 0" /> oraz <InlineMath math="x^2 - 4x + 4 \;>\; 0" /></p>

      <p><strong>Krok 1.</strong> Liczymy deltę i wyznaczamy punkt styczności.</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={
          String.raw`\begin{aligned}
          a&=1,\ b=-4,\ c=4\\
          \Delta&=b^2-4ac=16-16=0,\qquad x_0=\frac{-b}{2a}=\frac{4}{2}=2
          \end{aligned}`
        } />
      </div>

      <p><strong>Krok 2.</strong> Jeden wspólny szkic paraboli (ramiona do góry, styczność w 2):</p>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <ParabolaShape
          aSign={1}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          rootMode="one"
          tangentX={2}
          labelLeftTex="2"
          curveColor="#f59e0b"
        />
      </div>

      <p><strong>Wniosek 1 (≤ 0):</strong> tylko w punkcie styczności — <InlineMath math="\{2\}" />.</p>

      <p><strong>Wniosek 2 ( <InlineMath math=">" />0):</strong> wszędzie poza stycznością — <InlineMath math="\mathbb{R}\setminus\{2\}" />.</p>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <NumberLineRanges
          lockPair
          showZero
          ranges={[
            { to: 2, rightClosed: false, rightLabelLatex: "2" },
            { from: 2, leftClosed: false, leftLabelLatex: "2" },
          ]}
        />
      </div>
    </div>
  ),
},


    {
      label: "Przykład 4",
      summary: "Ramiona do góry, Δ<0 — f(x)>0 dla każdego x",
      description: "Rozwiąż nierówność:",
      content: (
        <div className="space-y-3">
          <p><strong>Polecenie:</strong> <InlineMath math="x^2 + 2x + 5 \;>\; 0" /></p>

          <p><strong>Delta:</strong> <InlineMath math="\Delta=2^2-4\cdot1\cdot5=4-20<0" /> — brak przecięć z OX.</p>

          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <ParabolaShape
              aSign={1}
              xRange={5}
              yWindow={5}
              showXAxis
              showZeroTicks
              rootMode="none"
              highlightMode="all"
              highlightColor="#a78bfa"
              curveColor="#f59e0b"
            />
          </div>

          <p><strong>Wniosek:</strong> <InlineMath math="x\in\mathbb{R}" />.</p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
